import {
  Box,
  Button,
  Collapse,
  Divider,
  HStack,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useTheme,
  VStack,
} from '@chakra-ui/react'
import {faRocketLaunch, faTrash} from '@fortawesome/pro-regular-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import isEmpty from 'lodash/isEmpty'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import {useRouter} from 'next/router'
import {ReactNode, useEffect, useState} from 'react'
import {COOKIE_INPUTS} from '../../../constants/cookies'
import {SUPPORTED_LANGUAGES} from '../../../constants/languages'
import {SELECTED_GENERATION_OPTION} from '../../../constants/playground'
import {SingleSkillFormData} from '../../../dataModels/SkillFormData'
import axios from '../../../fetchers/axiosAPI'
import {useAuth} from '../../../hooks/useAuth'
import {useInputsCookie} from '../../../hooks/useInputsCookie'
import {useSkill} from '../../../hooks/useSkill'
import {deleteCookieClient} from '../../../utils/cookieHelpers'
import {track} from '../../../utils/eventTracking/track'
import {getRandomIndex} from '../../../utils/randomHelpers'
import LoginModal from '../../Modals/LoginModal'
import SkillDoubleButton from './SkillDoubleButton'
import TextInput from './TextInput'

const ToxicWarningLight = dynamic(() => import('../../Modules/ToxicWarningLight'))
const GenerationLimitReachedModal = dynamic(
  () => import('../../Modals/GenerationLimitReachedModal')
)

interface Props {
  maxLength: number
  selectedOption?: any
  children?: ReactNode
}

const SkillSingleInputGenerateMorePlayground = ({maxLength, children, selectedOption}: Props) => {
  const toxicWarning = useDisclosure()
  const limitReached = useDisclosure()
  const connectionNeeded = useDisclosure()

  const theme = useTheme()
  const {skillExamples, skill} = useSkill<SingleSkillFormData>()
  const router = useRouter()
  const selectedLanguage = router.locale ?? 'en'
  const {t} = useTranslation('skill')

  const [input, setInput] = useState<string>('')
  const [output, setOutput] = useState<string>('')
  const [selectedGenerationType, setSelectedGenerationType] = useState<SELECTED_GENERATION_OPTION>(
    SELECTED_GENERATION_OPTION.GENERATE_FIRST
  )

  const {isLoading, setIsLoading, isToxic, setIsToxic} = useSkill()

  const displayExamplesButton = !isEmpty(skillExamples)
  const tooLong = input.length + output.length > maxLength

  const inputValue = useInputsCookie()

  useEffect(() => {
    setInput(inputValue)
    deleteCookieClient(COOKIE_INPUTS)
  }, [inputValue])

  const [randomExampleIndex, setRandomExampleIndex] = useState<number>(0)

  const getRandomExample = () => {
    setRandomExampleIndex(getRandomIndex(skillExamples, randomExampleIndex))
    const example = skillExamples[randomExampleIndex]
    setOutput('')
    setInput(t(`examples${selectedLanguage.toUpperCase()}:${example.input}`))
    setSelectedGenerationType(SELECTED_GENERATION_OPTION.GENERATE_FIRST)
  }

  const {publicUser} = useAuth().state

  useEffect(() => {
    isToxic ? toxicWarning.onOpen() : toxicWarning.onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToxic])

  const onGenerate = async () => {
    setSelectedGenerationType(SELECTED_GENERATION_OPTION.GENERATE_FIRST)
    await generateContent(input)
  }

  const onGenerateMore = async () => {
    setInput(input + output)
    setSelectedGenerationType(SELECTED_GENERATION_OPTION.GENERATE_FOLLOWING)
    await generateContent((input + output).slice((-maxLength * 2) / 3))
  }

  const onRedo = async () => {
    setInput(input)
    setSelectedGenerationType(SELECTED_GENERATION_OPTION.GENERATE_OTHER)
    await generateContent(input)
  }

  const generateContent = async (input: string) => {
    if (!publicUser || !publicUser.APIKey) {
      connectionNeeded.onOpen()
      return
    }
    setIsLoading(true)
    setIsToxic(false)
    setOutput('')
    try {
      const response = await axios.post(skill.endpoint, {
        input,
        ...selectedOption,
        textLanguage: selectedLanguage,
        locale: router.locale,
      })
      if (response && response.data.error) {
        const showToast = await import('../../../utils/showToast').then(mod => mod.showToast)
        showToast(response.data.error, 'error')
      }

      if (response && response.data.output) {
        setIsToxic(response.data.isToxic)
        setOutput(response.data.output)
      }
    } catch (e: any) {
      if (e.response?.status === 403) {
        limitReached.onOpen()
      } else {
        const showToast = await import('../../../utils/showToast').then(mod => mod.showToast)
        showToast(e.response?.data.error, 'error')
        return
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onDelete = () => {
    setSelectedGenerationType(SELECTED_GENERATION_OPTION.GENERATE_FIRST)
    setOutput('')
    setInput('')
  }

  return (
    <>
      {connectionNeeded.isOpen && (
        <LoginModal
          isOpen={connectionNeeded.isOpen}
          onClose={connectionNeeded.onClose}
          inputsToRecover={{input}}
          optionsToRecover={selectedOption && [selectedOption]}
          textLanguageToRecover={selectedLanguage as SUPPORTED_LANGUAGES}
        />
      )}
      <VStack
        p={5}
        spacing={5}
        background={theme.colors.layers.appLevel1}
        borderRadius={theme.radii.big}
        w='full'>
        {children}
        <VStack
          w='full'
          p={5}
          spacing={5}
          background={theme.colors.layers.appLevel2}
          borderRadius={theme.radii.big}
          justify='start'>
          <TextInput
            input={input}
            output={output}
            setInput={setInput}
            setOutput={setOutput}
            setSelectedGenerationType={setSelectedGenerationType}
          />
          <Box w='full' alignSelf='flex-end'>
            <Divider mb={4} mt={4} />
            <Stack
              direction={['column', null, null, 'row']}
              spacing={[5, null, null, 7]}
              w='full'
              align='center'
              justify='space-between'>
              <HStack w='full' justify='space-between' py={1}>
                <HStack spacing={4}>
                  <Tooltip label={t('gen_delete')} placement='top'>
                    <Button
                      variant='textIcon'
                      onClick={() => {
                        onDelete()
                      }}
                      isDisabled={isLoading}
                      aria-label='Delete content'>
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </Tooltip>
                  <Text
                    variant='mediumBold'
                    color={tooLong ? theme.colors.brand.error : theme.colors.text.grey}>
                    {input.length + output.length}/{maxLength}
                  </Text>
                </HStack>
                {displayExamplesButton && (
                  <Tooltip label={t('common:tooltip_examples')} placement='top'>
                    <Button
                      variant='textLarge'
                      leftIcon={<FontAwesomeIcon icon={faRocketLaunch} />}
                      onClick={() => {
                        getRandomExample()
                        track(`feature-click-${skill.skillName}-examples-button`)
                      }}
                      isDisabled={isLoading || selectedLanguage == SUPPORTED_LANGUAGES.de}>
                      {t('block_skill_input_button_example')}
                    </Button>
                  </Tooltip>
                )}
              </HStack>
              <SkillDoubleButton
                onRedo={onRedo}
                onGenerateMore={onGenerateMore}
                onGenerate={onGenerate}
                input={input}
                output={output}
                maxLength={maxLength}
                selectedGenerationType={selectedGenerationType}
                setSelectedGenerationType={setSelectedGenerationType}
              />
            </Stack>
          </Box>
        </VStack>
        {toxicWarning.isOpen && (
          <Box w='full'>
            <Collapse in={toxicWarning.isOpen} unmountOnExit animateOpacity>
              <ToxicWarningLight onClose={toxicWarning.onClose} />
            </Collapse>
          </Box>
        )}

        {limitReached.isOpen && (
          <GenerationLimitReachedModal
            isOpen={limitReached.isOpen}
            onClose={limitReached.onClose}
          />
        )}
      </VStack>
    </>
  )
}

export default SkillSingleInputGenerateMorePlayground
